import { CircleAlert, Inbox, RotateCw } from "lucide-react"
import type { ReactNode } from "react"

import { codigoDe, mensajeDe } from "@/lib/errores"

/**
 * Los tres estados de toda superficie asíncrona —cargando, vacío y error—, con UNA
 * forma en todo el sistema (12/10/2026).
 *
 * ⚠ **Antes cada pantalla los pintaba a su manera**: 66 «Cargando…», sólo 17 con
 * `role="status"`; unas 25 variantes de «No se pudo cargar…»; y un solo
 * «Reintentar» en todo el sistema. El mismo estado se leía distinto según dónde, y
 * un error de carga no dejaba más salida que recargar la página entera.
 *
 * El vacío de una TABLA sigue siendo `TablaVacia` (`Tabla.tsx`): va dentro de su
 * `<tbody>`. Esto es para todo lo demás.
 */

/** Barras con una veta de luz que cruza: dice «viene contenido» y su forma. */
export function EstadoCarga({
  etiqueta = "Cargando…",
  filas = 4,
}: {
  etiqueta?: string
  filas?: number
}) {
  return (
    <div role="status" className="grid gap-2.5 py-2">
      <span className="sr-only">{etiqueta}</span>
      {Array.from({ length: filas }, (_, i) => (
        <span
          // Las barras no cambian de orden ni de número: el índice es su identidad.
          // biome-ignore lint/suspicious/noArrayIndexKey: lista fija y decorativa
          key={i}
          aria-hidden="true"
          className="veta-carga block h-3.5 rounded-md bg-[rgb(var(--marco-tinta)/0.07)]"
          // La última más corta: se lee como texto, no como una rejilla.
          style={{ width: i === filas - 1 ? "62%" : "100%" }}
        />
      ))}
    </div>
  )
}

/**
 * Nada que enseñar, y qué hacer al respecto. ⚠ **Un vacío con filtros dice que son
 * los filtros** (ver ESTADO-SESION §9): quien llama pasa la `accion` de quitarlos.
 */
export function EstadoVacio({
  titulo,
  texto,
  accion,
}: {
  titulo: string
  texto?: string
  accion?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-4 py-7 text-center">
      <span
        aria-hidden="true"
        className="mb-1 grid size-10 place-items-center rounded-full bg-[rgb(var(--marco-tinta)/0.06)] text-[var(--fg-muted)]"
      >
        <Inbox size={18} />
      </span>
      <p className="font-semibold">{titulo}</p>
      {texto && <p className="max-w-[46ch] text-sm text-[var(--fg-muted)]">{texto}</p>}
      {accion && <div className="mt-2">{accion}</div>}
    </div>
  )
}

/**
 * Una carga que falló, con el mensaje del servidor, su código —lo que se dicta al
 * reportar— y «Reintentar». ⚠ **El reintento es la consulta, no la página**: lo que
 * ya estaba en pantalla no se pierde.
 */
export function EstadoError({
  titulo,
  error,
  onReintentar,
}: {
  titulo: string
  error: unknown
  onReintentar?: () => void
}) {
  const codigo = codigoDe(error)
  return (
    <div role="alert" className="flex flex-col items-center gap-1.5 px-4 py-7 text-center">
      <span
        aria-hidden="true"
        className="mb-1 grid size-10 place-items-center rounded-full bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] text-[var(--destructive)]"
      >
        <CircleAlert size={18} />
      </span>
      <p className="font-semibold">{titulo}</p>
      <p className="max-w-[46ch] text-sm text-[var(--fg-muted)]">
        {mensajeDe(
          error,
          "El servidor no respondió. Lo que ya estaba en pantalla no se ha perdido.",
        )}
      </p>
      {codigo && (
        <span className="rounded bg-[rgb(var(--marco-tinta)/0.08)] px-1.5 py-px font-mono text-[11px] text-[var(--fg-muted)]">
          {codigo}
        </span>
      )}
      {onReintentar && (
        <button
          type="button"
          onClick={onReintentar}
          className="boton-secundario foco-cristal mt-2 inline-flex items-center gap-1.5"
        >
          <RotateCw size={14} aria-hidden="true" />
          Reintentar
        </button>
      )}
    </div>
  )
}
